import { UgcAdScene } from '../../services/api/ugcAdService';
// remotion/ugc-ad の index 経由にすると deriveScenes ⇄ ここ で循環参照になるため直接読む
import { MIN_SCENE_SECONDS, computeSceneWindows } from '../../remotion/song-ad/sceneTiming';

/**
 * 映像トラック（シーン）編集の純ロジック。
 *
 * テロップ側（song-ad/timelineEditor.ts）と同じく、DOM操作から切り離して
 * 「尺のクランプ・並び替え・総尺の算出」だけをここに寄せる。React側は px⇔秒 の変換と
 * mousemove/mouseup の配線だけを担う。
 *
 * ## 設計
 * シーンは映像として必ず連続する（隙間が空けば黒画面、重なれば破綻する）。そのため
 * start/end を両方保存せず **各シーンの尺だけ** を timeline.scene_durations に持ち、
 * 開始位置は前のシーンの終端から導出する。不正な配置が構造的に発生しない。
 *
 * ## クリップ尺の上限
 * 動画クリップのあるシーンは、生成された動画の長さを超えて枠を伸ばせない。
 * 超えた分は合成時に最終フレームの複製で埋まる（＝止め絵が続く）ため、
 * 編集段階で止める方が親切という判断。静止画シーンは Ken Burns なので上限なし。
 */

/** シーンの最小尺。backend の UgcAdScene::MIN_DURATION_SECONDS と同値 */
export { MIN_SCENE_SECONDS };

/**
 * 1シーンの最大尺（秒）。backend の UgcAdProportionalTiming::MAX_SCENE_SECONDS と同値。
 *
 * 静止画シーンは Ken Burns なので理屈の上ではいくらでも伸ばせるが、書き出しでは
 * 4倍解像度の zoompan を尺ぶんのフレーム数だけ回すため、長くすると ffmpeg の
 * セグメントタイムアウト（300秒）や staging のメモリ（1.9GB）に当たる。
 * ショート動画として実用的な範囲でもあるのでここで頭打ちにする。
 */
export const MAX_SCENE_SECONDS = 30;

export type SceneDurations = Record<string, number>;

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * そのシーンを伸ばせる上限（秒）。
 *
 * 生成済みクリップを使うシーンはクリップの実尺まで。静止画で見せるシーンは
 * 引き延ばしても破綻しないので上限なし（Infinity）。
 * 「動画化」指定でもクリップが未生成なら、まだ実尺が分からないので制限しない。
 */
export function maxSceneSeconds(scene: UgcAdScene): number {
  if (scene.motion_mode !== 'clip') return MAX_SCENE_SECONDS;

  const clipSeconds = scene.clip.video?.duration_seconds;

  return typeof clipSeconds === 'number' && clipSeconds > 0
    ? Math.min(clipSeconds, MAX_SCENE_SECONDS)
    : MAX_SCENE_SECONDS;
}

/** そのシーンの尺を上限・下限に収める */
export function clampSceneSeconds(scene: UgcAdScene, seconds: number): number {
  const max = maxSceneSeconds(scene);

  // クリップが最小尺より短い異常ケースでも下限を割らないようにする
  return round3(clamp(seconds, MIN_SCENE_SECONDS, Math.max(MIN_SCENE_SECONDS, max)));
}

/**
 * 現在の尺（秒）をシーンIDごとに解決する。
 *
 * scene_durations に指定があればそれを、無ければ duration_sec 比の自動配分を使う。
 * 自動配分は編集前のプレビューと同じ規則（computeSceneWindows）なので、
 * 1シーンだけ手で伸ばしても他のシーンの見え方は変わらない。
 *
 * @param scenes  skip を除いた再生順のシーン列
 * @param durations  timeline.scene_durations
 * @param fallbackTotal  未指定シーンを按分するための総尺（target_seconds）
 */
export function resolveSceneSeconds(
  scenes: UgcAdScene[],
  durations: SceneDurations | undefined,
  fallbackTotal: number
): number[] {
  const auto = computeSceneWindows(
    scenes.map((s) => Math.max(1, s.duration_sec || 1)),
    fallbackTotal
  );

  return scenes.map((scene, i) => {
    const specified = durations?.[scene.id];

    if (typeof specified === 'number' && specified > 0) {
      return clampSceneSeconds(scene, specified);
    }

    const window = auto[i];
    const autoSeconds = window ? window.end - window.start : MIN_SCENE_SECONDS;

    // クリップが出来ているシーンは**実尺そのもの**を枠にする。
    // 上限だけを見ていた頃は、クリップが枠より長いと終わり際が切られて
    // 発話が途中で途切れ、テロップは按分どおり出るのでタイミングが食い違った
    // （UGC-08）。backend の UgcAdProportionalTiming::resolve と同じ規則にすること。
    const clipSeconds = scene.motion_mode === 'clip' ? scene.clip.video?.duration_seconds : undefined;

    if (typeof clipSeconds === 'number' && clipSeconds > 0) {
      return round3(Math.min(clipSeconds, MAX_SCENE_SECONDS));
    }

    // クリップがまだ無いシーンは従来どおり上限だけを見る
    return round3(Math.min(autoSeconds, maxSceneSeconds(scene)));
  });
}

/**
 * リサイズ結果を scene_durations に反映する。
 *
 * **編集した瞬間に全シーンの尺を確定させる**のが肝。1つだけ保存すると、残りは
 * 自動配分のままなので「総尺が変わる → 自動配分の按分先も変わる」で他のシーンが
 * 勝手に動いてしまう。編集を始めた時点の見た目をそのまま固定する。
 */
export function resizeSceneDuration(
  scenes: UgcAdScene[],
  durations: SceneDurations | undefined,
  fallbackTotal: number,
  sceneId: string,
  newSeconds: number
): SceneDurations {
  const current = resolveSceneSeconds(scenes, durations, fallbackTotal);
  const next: SceneDurations = {};

  scenes.forEach((scene, i) => {
    next[scene.id] = scene.id === sceneId ? clampSceneSeconds(scene, newSeconds) : current[i];
  });

  return next;
}

/** 配列の要素を from から to へ動かす（並び替え） */
export function reorder<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);

  return next;
}

/**
 * 尺の配列から、連続配置した開始・終了秒を作る
 */
export function toSceneWindows(seconds: number[]): { start: number; end: number }[] {
  const windows: { start: number; end: number }[] = [];
  let cursor = 0;

  seconds.forEach((sec) => {
    const start = cursor;
    const end = cursor + sec;
    cursor = end;
    windows.push({ start: round3(start), end: round3(end) });
  });

  return windows;
}

/**
 * 動画全体の長さ（秒）。
 *
 * scene_durations の指定が1つでもあれば各シーンの尺の合計（総尺可変）。
 * 何も指定が無ければ従来どおり target_seconds のまま。
 */
export function resolveTotalSeconds(
  scenes: UgcAdScene[],
  durations: SceneDurations | undefined,
  fallbackTotal: number
): number {
  const total = resolveSceneSeconds(scenes, durations, fallbackTotal).reduce((a, b) => a + b, 0);
  const hasEdit = scenes.some((s) => typeof durations?.[s.id] === 'number');

  // 手編集が無く、クリップ実尺の頭打ちも効いていないなら target_seconds のまま
  // （按分の丸め誤差で総尺が動かないようにする）
  if (!hasEdit && Math.abs(total - fallbackTotal) < 0.05) return fallbackTotal;

  return round3(Math.max(MIN_SCENE_SECONDS, total));
}
